import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import React, { ReactElement } from 'react';

import { IBidderGroup } from '~/src/dash/api/bidder-group';
import { IBidderGroupLabel } from '~/src/dash/api/bidder-group-label';
import { IChannelGroupLabel } from '~/src/dash/api/channel-group-label';
import { IChannelGroup } from '~/src/dash/api/console-api';
import { MaterialLabelDropdownMulti } from '~/src/dash/components/material-label-dropdown-multi';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { ISessionStateProps, IUserStateProps, IWithStylesProps } from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { siteWeightOptions } from '~/src/dash/utils/site-weights';

export interface IProps extends IWithStylesProps, ISessionStateProps, IUserStateProps {
  active: boolean;
  groupInfo?: IBidderGroup | IChannelGroup;
  list: IMenuList;
  menuTitle: string;
  showPrefixID: boolean;

  // quick filter related prop
  quickFilterOptions?: Array<{ title: string; optionItems: IMenuList; type: string }>;

  // Bidder / Channel Group Labels
  loadingGroup: boolean;
  allGroupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>;
  selectedGroupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>;
  handleChangeSelectedGroupLabels: (
    groupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>,
  ) => void;

  onClose?: () => void;
  onUpdate?: (name: string, ids: string, defaultWeight?: number) => void;
  onSave?: () => void;
  handleItemAcceptClick: (list: IMenuList) => string;
}

interface IState {
  Name: string;
  IDsValue: string;
  DefaultWeight?: number;
  list: IMenuList;
  [key: string]: any;
  helperText: string;
}

const getInitalIDValues = (groupInfo?: IBidderGroup | IChannelGroup): string => {
  if (groupInfo && 'BidderIDs' in groupInfo) {
    return groupInfo.BidderIDs;
  } else if (groupInfo && 'ChannelIDs' in groupInfo) {
    return groupInfo.ChannelIDs;
  }
  return '';
};

const getDefaultWeight = (groupInfo?: IBidderGroup | IChannelGroup): number | undefined => {
  if (groupInfo && 'ChannelIDs' in groupInfo) {
    if (groupInfo.DefaultWeight) {
      return groupInfo.DefaultWeight;
    }
    // If new channel group, set weight to default value of 1
    return 1;
  }
  return undefined;
};

export const GroupSettingsDialog = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      dialogPaper: {
        minHeight: '80vh',
        maxHeight: '80vh',
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      Name: this.props.groupInfo ? this.props.groupInfo.Name : '',
      IDsValue: getInitalIDValues(this.props.groupInfo),
      DefaultWeight: getDefaultWeight(this.props.groupInfo),
      list: this.props.list,
      helperText: '',
    };

    componentWillReceiveProps(nextProps: IProps) {
      if (this.props.groupInfo && nextProps.groupInfo) {
        // if the current and next group info ID is the same, use the state's Name
        if (this.props.groupInfo.ID === nextProps.groupInfo.ID) {
          this.setState({
            Name: this.state.Name,
            IDsValue: this.state.IDsValue,
            DefaultWeight: getDefaultWeight(nextProps.groupInfo),
            list: nextProps.active !== this.props.active ? nextProps.list : this.state.list,
          });
          return;
        }
      }
      this.setState({
        Name: nextProps.groupInfo ? nextProps.groupInfo.Name : '',
        IDsValue: getInitalIDValues(nextProps.groupInfo),
        DefaultWeight: getDefaultWeight(nextProps.groupInfo),
        list: nextProps.list,
      });
    }

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      this.setState(
        {
          [target.name]: target.value,
        },
        () => {
          if (this.state.helperText !== '' && this.state.Name !== '') {
            this.setState({
              helperText: '',
            });
          }
        },
      );
    };

    onClose = () => {
      if (this.props.onClose) {
        this.props.onClose();
      }
    };

    onUpdate = () => {
      const { DefaultWeight, IDsValue, Name } = this.state;
      if (Name === '') {
        this.setState({
          helperText: 'Please enter a group name before saving.',
        });
      } else {
        if (this.props.menuTitle && this.props.onUpdate) {
          if (this.props.menuTitle === 'Channel') {
            this.props.onUpdate(Name, IDsValue, DefaultWeight);
          } else if (this.props.menuTitle === 'Bidder' || this.props.menuTitle === 'Bidders') {
            this.props.onUpdate(Name, IDsValue);
          }
        }
        if (this.props.onSave) {
          this.props.onSave();
        }
      }
    };

    handleItemAcceptClick = (): void => {
      this.setState({
        IDsValue: this.props.handleItemAcceptClick(this.state.list),
      });
    };

    handleChangeDefaultWeight = (value: string) => {
      const weightNumber = parseInt(value, 10);
      this.setState({
        DefaultWeight: weightNumber,
      });
    };

    // renderGroupLabels renders the group labels used by both the bidder settings list
    // view & the channel settings list view
    renderGroupLabels = (): ReactElement | null => {
      const {
        allGroupLabels,
        handleChangeSelectedGroupLabels,
        loadingGroup,
        menuTitle,
        selectedGroupLabels,
      } = this.props;

      return (
        <MaterialLabelDropdownMulti
          loading={loadingGroup}
          menuTitle={`${menuTitle} Group Labels`}
          list={allGroupLabels || ([] as Array<IBidderGroupLabel | IChannelGroupLabel>)}
          selected={selectedGroupLabels || ([] as Array<IBidderGroupLabel | IChannelGroupLabel>)}
          onChange={(groupLabels: Array<IBidderGroupLabel | IChannelGroupLabel>) => {
            handleChangeSelectedGroupLabels(groupLabels);
          }}
        />
      );
    };

    render() {
      const { active, classes, menuTitle, quickFilterOptions } = this.props;

      const { helperText, list, DefaultWeight, Name } = this.state;

      const groupInfo = this.props.groupInfo || ({ ID: '' } as IBidderGroup | IChannelGroup);

      return (
        <Dialog
          classes={{ paper: classes.dialogPaper }}
          fullWidth
          maxWidth='md'
          open={active}
          onClose={this.onClose}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
        >
          <DialogTitle>
            {groupInfo && groupInfo.ID.toString() === '0'
              ? 'Creating New Group'
              : `Editing Group - ${Name} [${groupInfo.ID}]`}
          </DialogTitle>
          <DialogContent>
            <Grid className={classes.section}>
              <TextField
                label='Name'
                name='Name'
                style={{ width: '300px' }}
                value={Name}
                onChange={this.handleChange}
                error={helperText !== ''}
                helperText={helperText}
              />
            </Grid>
            <Grid className={classes.section}>
              <MaterialSearchDropdownMulti
                getValueFromSearchElement={(src: IListItem) => src.title}
                selectAll={true}
                menuTitle={menuTitle}
                list={list}
                onChange={(list: IMenuList) => this.setState({ list })}
                handleMenuAcceptClick={this.handleItemAcceptClick}
                quickFilterOptions={quickFilterOptions}
                showPrefixID={!!this.props.showPrefixID}
              />
            </Grid>
            {/* Bidder / Channel Group Labels */}
            {this.renderGroupLabels()}
            {this.props.session.publisher.ID === '788' && 'DefaultWeight' in groupInfo && (
              <Grid style={{ width: '400px' }}>
                <InputLabel id='default-cg-weight-label'>Default Channel Group Weight</InputLabel>
                <Select
                  labelId='default-cg-weight-label'
                  name='DefaultWeight'
                  style={{ marginRight: 20 }}
                  value={DefaultWeight}
                  onChange={(event: React.ChangeEvent<{ _: string; value: string }>) => {
                    this.handleChangeDefaultWeight(event.target.value);
                  }}
                >
                  {siteWeightOptions.map((w) => {
                    return <MenuItem value={w.value}>{w.label}</MenuItem>;
                  })}
                </Select>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.onClose} color='primary' autoFocus>
              Cancel
            </Button>
            <Button onClick={this.onUpdate} color='primary' autoFocus>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      );
    }
  },
);
